'use client';

import { getColorScheme } from '@/utils/getColorScheme';
import { getCTA } from '@/utils/getCTA';
import { getChildHeadingVariant } from '@/utils/headingLevel';
import { getVerticalSpacing } from '@/utils/getVerticalSpacing';
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { MarkDown } from './atoms/MarkDown';

/* ------------------------------ Helpers ------------------------------ */

function valOf(x, fallback = '') {
    if (x == null) return fallback;
    if (typeof x === 'object' && x && 'value' in x) return x.value ?? fallback;
    return x;
}
const trim = (s) => String(s ?? '').trim();

function normalizeAnimation(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    const map = {
        '': 'fade',
        none: 'none',
        fade: 'fade',
        'fade in': 'fade',
        slide: 'slide',
        'slide up': 'slide',
        zoom: 'zoom',
        'zoom in': 'zoom',
        stagger: 'stagger',
        cascade: 'stagger',
    };
    return map[v] ?? 'fade';
}

function mapGroup(raw) {
    const v = String(raw ?? '')
        .toLowerCase()
        .trim();
    if (
        ['ctas', 'ctas-only', 'cta', 'cta-only', 'buttons', 'button'].includes(
            v
        )
    ) {
        return 'ctas-only';
    }
    return 'text-content';
}

function getElementVariants(preset, duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    if (preset === 'slide') {
        return {
            offscreen: { opacity: 0, y: 16 },
            onscreen: {
                opacity: 1,
                y: 0,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    if (preset === 'zoom') {
        return {
            offscreen: { opacity: 0, scale: 0.96 },
            onscreen: {
                opacity: 1,
                scale: 1,
                transition: { type: 'tween', duration: d },
            },
        };
    }
    return {
        offscreen: { opacity: 0 },
        onscreen: { opacity: 1, transition: { type: 'tween', duration: d } },
    };
}

function getStaggerContainer(duration) {
    const d = Math.max(0.2, Number(duration) || 0.6);
    return {
        hidden: { opacity: 1 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: Math.min(0.25, d * 0.35),
                delayChildren: Math.min(0.3, d * 0.4),
            },
        },
    };
}

function MotionMaybe({ enable, k, motionProps, className, children }) {
    if (enable) {
        return (
            <motion.div key={`${k}-on`} className={className} {...motionProps}>
                {children}
            </motion.div>
        );
    }
    return (
        <div
            key={`${k}-off`}
            className={className}
            style={{ opacity: '', transform: '' }}
        >
            {children}
        </div>
    );
}

// Same alignment API as USP for placement, but item text stays left-aligned
function getAlignmentClasses(alignment) {
    switch (String(alignment || 'left').toLowerCase()) {
        case 'center':
            return {
                // placement of the question blocks
                blockPlacement: 'justify-center',
                // CTA alignment
                buttonAlign: 'justify-center',
                // container alignment for headline/tagline
                containerAlign: 'mx-auto',
                // headline/tagline text align (can still center)
                headlineTextAlign: 'text-center',
            };
        case 'right':
            return {
                blockPlacement: 'justify-end',
                buttonAlign: 'justify-end',
                containerAlign: 'ml-auto',
                headlineTextAlign: 'text-right',
            };
        case 'left':
        default:
            return {
                blockPlacement: 'justify-start',
                buttonAlign: 'justify-start',
                containerAlign: '',
                headlineTextAlign: 'text-left',
            };
    }
}

// Smooth expand/collapse variants for the answer
const answerVariants = {
    collapsed: { height: 0, opacity: 0 },
    open: { height: 'auto', opacity: 1 },
};
const answerTransition = {
    duration: 0.28,
    ease: [0.22, 0.61, 0.36, 1], // standard UI ease
};

/* -------------------------------- Component -------------------------------- */

export default function Faq(props) {
    // Content
    const Title = trim(valOf(props.Header, valOf(props.Title, '')));
    const Tagline = trim(valOf(props.Tagline, '')); // USP-style tagline
    const FAQs = Array.isArray(props.FAQs) ? props.FAQs.slice(0, 20) : [];
    const questionHeadingVariant = getChildHeadingVariant(Title);

    // Display
    const ColorScheme = valOf(props.ColorScheme, 'white');
    const VerticalSpacing = valOf(props.VerticalSpacing, 'medium');
    const Alignment = valOf(props.Alignment, 'center'); // center by default (like USP)
    const Layout = String(valOf(props.Layout, 'Single-Column')).toLowerCase();
    const isTwoColumnLayout = [
        'two-column',
        'two column',
        'twocolumn',
        'columns',
    ].includes(Layout);

    // Animation (grouped)
    const Animation = normalizeAnimation(valOf(props.Animation, 'stagger'));
    const Duration = Number(valOf(props.Duration, 0.6)) || 0.6;
    const EditorPreview = Boolean(valOf(props.EditorPreview, false));
    const requestedGroup = mapGroup(
        valOf(props.AnimationGroup, 'text-content')
    );

    // CTA
    const CTA = trim(valOf(props.CTA, ''));
    const CustomCTALink = trim(valOf(props.CustomCTALink, ''));
    const SecondaryButtonText = trim(valOf(props.SecondaryButtonText, ''));
    const SecondaryButtonLink = trim(valOf(props.SecondaryButtonLink, ''));
    const OutlineButtonText = trim(valOf(props.OutlineButtonText, ''));
    const OutlineButtonLink = trim(valOf(props.OutlineButtonLink, ''));
    const hasAnyCTA = Boolean(CTA || SecondaryButtonText || OutlineButtonText);

    // Final animation group (mirrors USP)
    const AnimationGroup =
        requestedGroup === 'ctas-only' && !hasAnyCTA
            ? 'text-content'
            : requestedGroup;
    const animateText = AnimationGroup === 'text-content';
    const animateCTAs = AnimationGroup === 'ctas-only';

    // Colors + spacing
    const {
        backgroundColor,
        textColor,
        dividerColor,
        headerColor,
        taglineColor,
        secondaryButton,
        thirdButton,
        backgroundStyle,
    } = getColorScheme(ColorScheme, props.GradientDirection);
    const vSpace = getVerticalSpacing(VerticalSpacing);
    const { blockPlacement, buttonAlign, containerAlign, headlineTextAlign } =
        getAlignmentClasses(Alignment);
    const isDarkSurface = textColor.includes('text-white');
    const sectionMaxWidth = isTwoColumnLayout ? 'max-w-7xl' : 'max-w-6xl';
    const contentMaxWidth = isTwoColumnLayout ? 'max-w-6xl' : 'max-w-4xl';
    const listLayoutClass = isTwoColumnLayout
        ? 'mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:gap-6'
        : 'mt-10 space-y-6';
    const itemOuterClass = isTwoColumnLayout ? '' : `flex ${blockPlacement}`;
    const itemPanelClass = isTwoColumnLayout
        ? `w-full rounded-[var(--shape-radius)] border px-5 py-5 shadow-sm sm:px-6 ${
              isDarkSurface
                  ? 'border-white/20 bg-white/10'
                  : 'border-neutral-300 bg-white'
          }`
        : `w-full max-w-2xl border-t ${dividerColor} pt-6`;
    const questionButtonClass = isTwoColumnLayout
        ? `flex min-h-[3.5rem] w-full items-center justify-between text-left ${textColor}`
        : `flex w-full items-start justify-between text-left ${textColor}`;

    // Motion prefs
    const [prefersReduced, setPrefersReduced] = useState(false);
    useEffect(() => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
            const update = (e) => setPrefersReduced(e.matches);
            setPrefersReduced(mq.matches);
            mq.addEventListener?.('change', update);
            return () => mq.removeEventListener?.('change', update);
        }
    }, []);
    const preset = Animation;
    const isStagger = preset === 'stagger';
    const enableAnim = !prefersReduced && preset !== 'none';
    const viewport = { once: !EditorPreview, amount: 0.45 };
    const baseKey = `faq-${AnimationGroup}-${preset}-${Duration}-${
        EditorPreview ? 'preview' : 'prod'
    }`;

    // Containers (stagger)
    const containerMotionProps = useMemo(() => {
        if (!enableAnim || !isStagger) return {};
        const variants = getStaggerContainer(Duration);
        return EditorPreview
            ? { variants, initial: 'hidden', animate: 'show' }
            : { variants, initial: 'hidden', whileInView: 'show', viewport };
    }, [enableAnim, isStagger, Duration, EditorPreview]);

    // Children motion
    const childMotionProps = useMemo(() => {
        if (!enableAnim) return {};
        if (isStagger) {
            return {
                variants: {
                    hidden: { opacity: 0, y: 16 },
                    show: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: Math.max(0.4, Duration - 0.2) },
                    },
                },
            };
        }
        const v = getElementVariants(preset, Duration);
        const hasOffscreen = v && 'offscreen' in v;
        const initialState = hasOffscreen ? 'offscreen' : 'hidden';
        const targetState = hasOffscreen ? 'onscreen' : 'show';
        return EditorPreview
            ? { variants: v, initial: initialState, animate: targetState }
            : {
                  variants: v,
                  initial: initialState,
                  whileInView: targetState,
                  viewport,
              };
    }, [enableAnim, preset, isStagger, Duration, EditorPreview]);

    // FAQ open state
    const [openIndexes, setOpenIndexes] = useState([]);
    const toggleIndex = (index) => {
        setOpenIndexes((prev) =>
            prev.includes(index)
                ? prev.filter((i) => i !== index)
                : [...prev, index]
        );
    };

    // CTAs (aligned like USP)
    const { hasAny, element: CTAs } = getCTA(
        {
            ...props,
            CTA,
            CustomCTALink,
            SecondaryButtonText,
            SecondaryButtonLink,
            OutlineButtonText,
            OutlineButtonLink,
        },
        {
            secondaryVariant: secondaryButton,
            outlineVariant: thirdButton,
            buttonAlign, // alignment from props (left/center/right)
            enableAnim: enableAnim && animateCTAs,
            motionProps: childMotionProps,
        }
    );

    return (
        <section
            id="faq"
            className={`editable-component relative ${backgroundColor} ${vSpace}`}
            style={backgroundStyle}
        >
            <div
                className={`relative z-40 mx-auto ${sectionMaxWidth} px-6 lg:px-8`}
            >
                <div className={`mx-auto ${contentMaxWidth} ${containerAlign}`}>
                    {/* GROUP 1: Tagline + Title */}
                    <MotionMaybe
                        enable={enableAnim && isStagger && animateText}
                        k={`${baseKey}-intro-container`}
                        motionProps={containerMotionProps}
                        className={`${headlineTextAlign}`}
                    >
                        {Tagline ? (
                            <MotionMaybe
                                enable={enableAnim && animateText}
                                k={`${baseKey}-tagline`}
                                motionProps={childMotionProps}
                            >
                                <MarkDown
                                    className={`max-w-2xl text-base font-semibold uppercase leading-7 tracking-wide ${taglineColor} ${containerAlign}`}
                                >
                                    {Tagline}
                                </MarkDown>
                            </MotionMaybe>
                        ) : null}

                        {Title ? (
                            <MotionMaybe
                                enable={enableAnim && animateText}
                                k={`${baseKey}-title`}
                                motionProps={childMotionProps}
                            >
                                <MarkDown
                                    variant="h2"
                                    className={`font-display text-4xl leading-10 tracking-tight ${headerColor} ${headlineTextAlign}`}
                                >
                                    {Title}
                                </MarkDown>
                            </MotionMaybe>
                        ) : null}
                    </MotionMaybe>

                    {/* GROUP 2: FAQ list (each item block is placed left/center/right, but text stays left) */}
                    <MotionMaybe
                        enable={enableAnim && isStagger && animateText}
                        k={`${baseKey}-list-container`}
                        motionProps={containerMotionProps}
                        className={listLayoutClass}
                    >
                        {FAQs.map((FAQ, index) => {
                            const isOpen = openIndexes.includes(index);
                            const qid = `faq-q-${index}`;
                            const pid = `faq-panel-${index}`;

                            return (
                                <div
                                    key={`${FAQ?.Question ?? index}`}
                                    className={itemOuterClass}
                                >
                                    <MotionMaybe
                                        enable={enableAnim && animateText}
                                        k={`${baseKey}-item-${index}`}
                                        motionProps={childMotionProps}
                                        // The centered/right-placed item column; text inside remains left-aligned
                                        className={itemPanelClass}
                                    >
                                        <div>
                                            <button
                                                type="button"
                                                id={qid}
                                                onClick={() =>
                                                    toggleIndex(index)
                                                }
                                                className={questionButtonClass}
                                                aria-expanded={isOpen}
                                                aria-controls={pid}
                                            >
                                                <MarkDown
                                                    variant={
                                                        questionHeadingVariant
                                                    }
                                                    className="text-left text-xl font-text leading-7"
                                                >
                                                    {FAQ?.Question}
                                                </MarkDown>

                                                {/* Smooth icon rotation */}
                                                <motion.span
                                                    className="ml-6 flex h-7 items-center"
                                                    animate={{
                                                        rotate: isOpen
                                                            ? 180
                                                            : 0,
                                                    }}
                                                    transition={{
                                                        duration: 0.2,
                                                    }}
                                                >
                                                    {isOpen ? (
                                                        <MinusSmallIcon
                                                            className="h-6 w-6"
                                                            aria-hidden="true"
                                                        />
                                                    ) : (
                                                        <PlusSmallIcon
                                                            className="h-6 w-6"
                                                            aria-hidden="true"
                                                        />
                                                    )}
                                                </motion.span>
                                            </button>
                                        </div>

                                        <motion.div
                                            id={pid}
                                            role="region"
                                            aria-labelledby={qid}
                                            aria-hidden={!isOpen}
                                            className="overflow-hidden pr-12 text-left"
                                            initial={false}
                                            animate={
                                                isOpen ? 'open' : 'collapsed'
                                            }
                                            variants={answerVariants}
                                            transition={answerTransition}
                                        >
                                            <MarkDown
                                                className={`mt-2 text-base font-text leading-7 ${textColor}`}
                                            >
                                                {FAQ?.Answer}
                                            </MarkDown>
                                        </motion.div>
                                    </MotionMaybe>
                                </div>
                            );
                        })}
                    </MotionMaybe>

                    {/* GROUP 3: CTAs (aligned like USP) */}
                    {hasAny && (
                        <MotionMaybe
                            enable={enableAnim && animateCTAs}
                            k={`${baseKey}-ctas`}
                            motionProps={childMotionProps}
                            className={`mt-10 flex ${buttonAlign} lg:mt-16`}
                        >
                            {CTAs}
                        </MotionMaybe>
                    )}
                </div>
            </div>
        </section>
    );
}
